import type { RecruiteeCandidateData } from './interfaces'
import type { AxiosRequestConfig } from 'axios'

import axios from 'axios'

import { useCommonStore } from '@shared/model'
import { getRecruiteeFieldsUrl, getRecruiteeSourcesUrl, getRecruiteeUrl } from '@shared/lib/links'
import { getSourceTypeFromUrl } from '@shared/lib/source'

import { useTutorApplicationStore } from '../../model/store'
import { useHiringApi } from '../hiring/hiring.hook'
import { useTrackRequest } from '../track-request.hook'
import {
  amplitudeId,
  demoOfferSlug,
  demoQuestions,
  demoQuestionsListFlag,
  elaOfferSlug,
  elaQuestions,
  elaQuestionsListFlag,
  mathOfferSlug,
  mathQuestions,
  mathQuestionsListFlag,
} from './recruitee'

const EVENT_NAME = 'modal_tutor_application'

type QuestionMap = Record<string, string>

interface LandingConfig {
  offerSlug: string
  questions: QuestionMap
  questionsListFlag: QuestionMap
}

interface ReturnRecruiteeApi {
  submitCandidate: (candidateData: RecruiteeCandidateData) => Promise<void>
}

export const useRecruiteeApi = (): ReturnRecruiteeApi => {
  const runtimeConfig = useRuntimeConfig().public
  const commonStore = useCommonStore()
  const tutorApplicationStore = useTutorApplicationStore()
  const { saveTutorTracking } = useHiringApi()
  const { trackSuccess, trackError } = useTrackRequest(EVENT_NAME)

  const API_TOKEN = runtimeConfig.recruiteeApiToken
  const COMPANY_ID = runtimeConfig.recruiteeCompanyId

  const authConfig: AxiosRequestConfig = {
    headers: { Authorization: `Bearer ${API_TOKEN}` },
  }

  const resolveLandingConfig = (): LandingConfig | null => {
    if (commonStore.isMathLanding) {
      return { offerSlug: mathOfferSlug, questions: mathQuestions, questionsListFlag: mathQuestionsListFlag }
    }
    if (commonStore.isElaLanding) {
      return { offerSlug: elaOfferSlug, questions: elaQuestions, questionsListFlag: elaQuestionsListFlag }
    }
    if (commonStore.isDemoLanding) {
      return { offerSlug: demoOfferSlug, questions: demoQuestions, questionsListFlag: demoQuestionsListFlag }
    }
    return null
  }

  const appendQuestions = (
    formData: FormData,
    candidateData: RecruiteeCandidateData,
    questions: QuestionMap,
    isFlag = false,
  ): void => {
    const valueField = isFlag ? 'flag' : 'content'

    Object.entries(questions).forEach(([key, id]) => {
      const value = candidateData[key as keyof RecruiteeCandidateData]
      if (value === undefined || value === null || value === '') return

      const base = `candidate[open_question_answers_attributes][${id}]`
      formData.append(`${base}[open_question_id]`, id)
      formData.append(`${base}[${valueField}]`, String(value))
    })
  }

  const buildFormData = (candidateData: RecruiteeCandidateData, config: LandingConfig): FormData => {
    const formData = new FormData()

    formData.append('candidate[name]', candidateData.name)
    formData.append('candidate[email]', candidateData.email)
    formData.append('candidate[cv]', candidateData.cv as Blob)
    formData.append('candidate[phone]', candidateData.phone)

    if (candidateData.cover_letter_file) {
      formData.append('candidate[cover_letter_file]', candidateData.cover_letter_file as Blob)
    }

    appendQuestions(formData, candidateData, config.questions)
    appendQuestions(formData, candidateData, config.questionsListFlag, true)

    return formData
  }

  const enrichCandidate = (candidateId: string): void => {
    const urlSources = getRecruiteeSourcesUrl(COMPANY_ID, candidateId)
    const urlFields = getRecruiteeFieldsUrl(COMPANY_ID, candidateId)
    const requests: Promise<unknown>[] = []

    if (urlSources) {
      requests.push(axios.post(urlSources, { sources: getSourceTypeFromUrl() }, authConfig))
    }

    if (urlFields && tutorApplicationStore.amplitudeId) {
      requests.push(axios.post(urlFields, {
        field: {
          profile_field_id: amplitudeId,
          kind: 'single_line',
          name: 'Amplitude id',
          values: [{ text: tutorApplicationStore.amplitudeId }],
        },
      }, authConfig))
    }

    if (!requests.length) return

    Promise.allSettled(requests).then((results) => {
      results.forEach((r) => {
        if (r.status === 'rejected') trackError(r.reason)
      })
    })
  }

  const submitCandidate = async (candidateData: RecruiteeCandidateData): Promise<void> => {
    const config = resolveLandingConfig()
    const url = config ? getRecruiteeUrl(config.offerSlug) : null

    if (!config || !url) {
      console.warn('Missing url for recruitee api')
      return
    }

    const formData = buildFormData(candidateData, config)
    commonStore.setLoading(true)

    try {
      const res = await axios.post(url, formData, authConfig)

      trackSuccess({ data: JSON.stringify(candidateData) })

      enrichCandidate(res.data.candidate.id)
      saveTutorTracking({
        email: candidateData.email,
        recruitee_id: String(res.data.candidate.id),
        amplitude_id: tutorApplicationStore.amplitudeId || null,
      })
    } catch (err: any) {
      trackError(err)
      throw new Error(err.response?.data?.message || err?.message || 'Unexpected error', { cause: err })
    } finally {
      commonStore.setLoading(false)
    }
  }

  return {
    submitCandidate,
  }
}
